import React, { useEffect, useRef } from "react";

/**
 * items: [{ type: "image"|"video", src: "/path", alt?: string, poster?: string }]
 * index: number (current item)
 * onClose: fn()
 * onPrev: fn()
 * onNext: fn()
 */
export default function MediaLightbox({ items, index, onClose, onPrev, onNext }) {
  const containerRef = useRef(null);

  // Keyboard handlers: Esc, ←, →
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const item = items[index];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      ref={containerRef}
      onClick={(e) => {
        // click outside content closes
        if (e.target === containerRef.current) onClose();
      }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 p-2 text-white"
      >
        ✕
      </button>

      {/* Prev / Next */}
      <button
        onClick={onPrev}
        aria-label="Previous"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 p-3 text-white"
      >
        ◀
      </button>
      <button
        onClick={onNext}
        aria-label="Next"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 p-3 text-white"
      >
        ▶
      </button>

      {/* Media */}
      <div className="w-[92vw] h-[80vh] flex items-center justify-center">
        {item.type === "image" ? (
          <img
            key={item.src}
            src={item.src}
            alt={item.alt || ""}
            className="max-h-full max-w-full object-contain"
            draggable={false}
          />
        ) : (
          <video
            key={item.src} // re-mount on change so playback resets
            src={item.src}
            poster={item.poster}
            className="max-h-full max-w-full object-contain"
            controls
            autoPlay
          />
        )}
      </div>
    </div>
  );
}
